import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Check, X, Lock, Unlock } from 'lucide-react';
import { useSelections } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
function MarqueeName({
  name,
  isDark
}: {
  name: string;
  isDark: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (containerRef.current && textRef.current) {
      const diff = textRef.current.scrollWidth - containerRef.current.clientWidth;
      setOverflow(diff > 2 ? diff + 8 : 0);
    }
  }, [name]);
  return <div ref={containerRef} className="overflow-hidden px-1" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-766">
      <span ref={textRef} className={`block whitespace-nowrap text-[9px] font-bold text-center uppercase tracking-wider ${isDark ? 'text-white/90' : 'text-gray-800'}`} style={{
      transform: isHovered && overflow > 0 ? `translateX(-${overflow}px)` : 'translateX(0)',
      transition: isHovered && overflow > 0 ? `transform ${Math.max(1, overflow / 40)}s ease-in-out` : 'transform 0.3s ease-out'
    }} title={name} data-id="element-767">
        {name}
      </span>
    </div>;
}
interface GlobalPackageGalleryProps {
  onClose: () => void;
  docked?: boolean;
  defaultPackageId?: string;
}
interface PackageItem {
  category: string;
  name: string;
  thumbnail: string;
}
interface GlobalPackageTier {
  id: string;
  name: string;
  label: string;
  price: number;
  originalPrice: number;
  description: string;
  badgeColor: string;
  items: PackageItem[];
}
const T = {
  a: 'https://cdn.magicpatterns.com/uploads/56xcQygN48ytC9vCWfbKsD/2a3e8f16-fd9a-4893-b4aa-68846e8eee8d.png',
  b: 'https://cdn.magicpatterns.com/uploads/sj8L76yNDFjCGFZWdgGY4N/9d9925e9-c3ea-4a0d-80fb-ffcfee4e866e.png',
  c: 'https://cdn.magicpatterns.com/uploads/4rsTQoSw8cboz4u88BHB6e/31666bba-be57-4389-96b2-845051abc30a.png',
  d: 'https://cdn.magicpatterns.com/uploads/8uMrohCuZZGCZe6mp1Ebh5/bdab08d5-af5f-4768-afba-d5b3c4e4d4f4.png',
  e: 'https://cdn.magicpatterns.com/uploads/rWjZv6Mm4vUpRox9hUrK2K/92c595c0-b3df-4f96-84f9-414aad4eea5b.png',
  f: 'https://cdn.magicpatterns.com/uploads/cZReokz7WQeKktQan8Tx5j/c4f38fb8-8ad6-4d2b-ba7a-5cae3db41a44.png',
  g: 'https://cdn.magicpatterns.com/uploads/mVj6c7zkUqrRquwCmxWcL6/15d95c32-8149-4132-9785-f1bfe49bb891.png',
  h: 'https://cdn.magicpatterns.com/uploads/kpH5AVoc585CmkhtFwT2gu/bb2e92e2-95c2-4917-a8f9-e317d073068e.png',
  i: 'https://cdn.magicpatterns.com/uploads/hA4ppMCeeEnfUr8DdemFhK/80b42e57-750f-436c-8070-ab27d0716845.png',
  j: 'https://cdn.magicpatterns.com/uploads/xefSXa2PcHLjXAgiprF9C2/94335722-d546-4e85-83cf-af86687637c9.png'
};
const GLOBAL_PACKAGES: GlobalPackageTier[] = [{
  id: 'gpkg-sky',
  name: 'Sky',
  label: 'Sky',
  price: 0,
  originalPrice: 0,
  description: 'Light, airy, modern. Cool blues and whites.',
  badgeColor: 'bg-sky-500',
  items: [{
    category: 'Cabinet Profile',
    name: 'White Shaker',
    thumbnail: T.g
  }, {
    category: 'Countertop',
    name: 'White Quartz',
    thumbnail: T.i
  }, {
    category: 'Handle Material',
    name: 'Brushed Nickel Bar',
    thumbnail: T.a
  }, {
    category: 'Interior Door Hardware',
    name: 'Satin Nickel Lever',
    thumbnail: T.b
  }, {
    category: 'Bathroom Counter',
    name: 'White Marble',
    thumbnail: T.j
  }, {
    category: 'Bathroom Cabinetry',
    name: 'White Shaker',
    thumbnail: T.g
  }, {
    category: 'Paint',
    name: 'Cloud White',
    thumbnail: T.e
  }, {
    category: 'Carpet',
    name: 'Light Gray Plush',
    thumbnail: T.c
  }, {
    category: 'Flooring',
    name: 'Light Oak LVP',
    thumbnail: T.h
  }, {
    category: 'Ensuite Tile',
    name: 'White Subway',
    thumbnail: T.f
  }]
}, {
  id: 'gpkg-river',
  name: 'River',
  label: 'River',
  price: 8500,
  originalPrice: 9500,
  description: 'Warm neutrals, natural wood tones, earthy.',
  badgeColor: 'bg-cyan-700',
  items: [{
    category: 'Cabinet Profile',
    name: 'Natural Oak',
    thumbnail: T.h
  }, {
    category: 'Countertop',
    name: 'Butcher Block',
    thumbnail: T.d
  }, {
    category: 'Handle Material',
    name: 'Matte Black Pull',
    thumbnail: T.c
  }, {
    category: 'Interior Door Hardware',
    name: 'Oil Bronze Lever',
    thumbnail: T.f
  }, {
    category: 'Bathroom Counter',
    name: 'Warm Granite',
    thumbnail: T.a
  }, {
    category: 'Bathroom Cabinetry',
    name: 'Walnut Stain',
    thumbnail: T.i
  }, {
    category: 'Paint',
    name: 'Warm Linen',
    thumbnail: T.b
  }, {
    category: 'Carpet',
    name: 'Taupe Berber',
    thumbnail: T.g
  }, {
    category: 'Flooring',
    name: 'Walnut LVP',
    thumbnail: T.j
  }, {
    category: 'Ensuite Tile',
    name: 'Warm Gray Herringbone',
    thumbnail: T.e
  }]
}, {
  id: 'gpkg-forest',
  name: 'Forest',
  label: 'Forest',
  price: 14200,
  originalPrice: 16000,
  description: 'Rich, dark, dramatic. Deep greens and dark woods.',
  badgeColor: 'bg-emerald-700',
  items: [{
    category: 'Cabinet Profile',
    name: 'Dark Espresso',
    thumbnail: T.j
  }, {
    category: 'Countertop',
    name: 'Black Granite',
    thumbnail: T.a
  }, {
    category: 'Handle Material',
    name: 'Antique Brass Knob',
    thumbnail: T.b
  }, {
    category: 'Interior Door Hardware',
    name: 'Aged Bronze Lever',
    thumbnail: T.c
  }, {
    category: 'Bathroom Counter',
    name: 'Dark Soapstone',
    thumbnail: T.d
  }, {
    category: 'Bathroom Cabinetry',
    name: 'Forest Green',
    thumbnail: T.e
  }, {
    category: 'Paint',
    name: 'Deep Sage',
    thumbnail: T.f
  }, {
    category: 'Carpet',
    name: 'Charcoal Twist',
    thumbnail: T.g
  }, {
    category: 'Flooring',
    name: 'Dark Hickory LVP',
    thumbnail: T.h
  }, {
    category: 'Ensuite Tile',
    name: 'Dark Slate Mosaic',
    thumbnail: T.i
  }]
}];
export function GlobalPackageGallery({
  onClose,
  docked = false,
  defaultPackageId
}: GlobalPackageGalleryProps) {
  const {
    activePackage,
    setActivePackage,
    clearActivePackage,
    getOverrideCount,
    resetPackageOverrides,
    isItemOverridingPackage
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const [viewedPackageId, setViewedPackageId] = useState<string>(activePackage?.id || defaultPackageId || 'gpkg-sky');
  const [isMinimized, setIsMinimized] = useState(false);
  // Update viewed package if active package changes externally
  useEffect(() => {
    if (activePackage?.id && activePackage.id.startsWith('gpkg-')) {
      setViewedPackageId(activePackage.id);
    }
  }, [activePackage]);
  // Update viewed package when sidebar selection changes
  useEffect(() => {
    if (defaultPackageId && GLOBAL_PACKAGES.some(p => p.id === defaultPackageId)) {
      setViewedPackageId(defaultPackageId);
    }
  }, [defaultPackageId]);
  const viewedPackage = GLOBAL_PACKAGES.find(p => p.id === viewedPackageId) || GLOBAL_PACKAGES[0];
  const isActive = activePackage?.id === viewedPackageId;
  const savings = viewedPackage.originalPrice - viewedPackage.price;
  const handleSelectPackage = () => {
    setActivePackage({
      id: viewedPackage.id,
      tier: viewedPackage.name,
      category: 'Global',
      price: viewedPackage.price,
      items: viewedPackage.items.map(item => ({
        itemName: item.category,
        optionId: `gpkg-${viewedPackage.id}-${item.category.toLowerCase().replace(/\s+/g, '-')}`,
        optionName: item.name,
        price: 0,
        thumbnail: item.thumbnail
      }))
    });
  };
  const handleRemovePackage = () => {
    clearActivePackage();
  };
  return <div className={docked ? 'w-full pointer-events-none' : `absolute left-[260px] right-0 z-30 flex flex-col items-center justify-end pointer-events-none px-6 ${isMinimized ? 'bottom-[48px]' : 'bottom-[60px]'}`} data-id="element-768">
      <AnimatePresence mode="wait" data-id="element-769">
        {isMinimized ? <motion.button key="minimized-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={() => setIsMinimized(false)} className={`pointer-events-auto flex items-center gap-2 px-5 py-2.5 rounded-t-xl backdrop-blur-xl border border-b-0 shadow-lg transition-colors ${isDark ? 'bg-slate-900/90 border-white/10 hover:bg-slate-800/90 text-white/70 hover:text-white' : 'bg-white/90 border-gray-200 hover:bg-white text-gray-500 hover:text-gray-700'}`} data-id="element-770">
            <ChevronUp className="w-4 h-4" data-id="element-771" />
            <span className="text-[11px] font-bold uppercase tracking-wider" data-id="element-772">
              Global Packages
            </span>
            <ChevronUp className="w-4 h-4" data-id="element-773" />
          </motion.button> : <motion.div key="full-gallery" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className={`backdrop-blur-xl border shadow-2xl pointer-events-auto overflow-hidden transition-all duration-300 flex flex-col
              ${docked ? 'w-full rounded-t-2xl border-b-0' : 'w-full max-w-5xl rounded-2xl'} 
              ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/85 border-white/40'}`} data-id="element-774">
            {/* Header */}
            <div className={`flex items-center gap-3 px-4 py-2 border-b flex-shrink-0 ${isDark ? 'border-white/5 bg-white/5' : 'border-gray-200/50 bg-gray-50/50'}`} data-id="element-775">
              <span className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-776">
                Global Packages
              </span>

              {/* Tier Pills */}
              <div className="flex items-center gap-2 flex-wrap" data-id="element-777">
                {GLOBAL_PACKAGES.map(pkg => {
              const isSelected = viewedPackageId === pkg.id;
              const isPackageActive = activePackage?.id === pkg.id;
              return <button key={pkg.id} onClick={() => setViewedPackageId(pkg.id)} className={`
                        flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap border
                        ${isSelected ? isDark ? 'bg-cyan-500 text-white border-cyan-400 shadow-lg shadow-cyan-500/20' : 'bg-teal-600 text-white border-teal-500 shadow-lg shadow-teal-600/20' : isDark ? 'bg-white/5 text-white/60 border-transparent hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200 hover:text-gray-900'}
                      `} data-id="element-778">
                      {pkg.label}
                      {isPackageActive && <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" data-id="element-779" />}
                    </button>;
            })}
              </div>

              <div className="flex-1" data-id="element-780" />

              <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-781">
                <button onClick={() => setIsMinimized(true)} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} title="Minimize" data-id="element-782">
                  <ChevronDown className="w-4 h-4" data-id="element-783" />
                </button>
                <button onClick={onClose} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} title="Close" data-id="element-784">
                  <X className="w-4 h-4" data-id="element-785" />
                </button>
              </div>
            </div>

            {/* Package Info Bar */}
            <div className={`flex items-center justify-between px-4 py-2 border-b flex-shrink-0 ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-786">
              <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-787">
                <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-white flex-shrink-0 ${isActive && getOverrideCount() > 0 ? 'bg-amber-500' : viewedPackage.badgeColor}`} data-id="element-788">
                  {viewedPackage.name}{' '}
                  {isActive && getOverrideCount() > 0 && '· MODIFIED'}
                </span>
                <span className={`text-xs truncate ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-789">
                  {isActive && getOverrideCount() > 0 ? `${getOverrideCount()} item${getOverrideCount() === 1 ? '' : 's'} customized` : viewedPackage.description}
                </span>
              </div>

              <div className="flex items-center gap-4 flex-shrink-0" data-id="element-790">
                {isActive && getOverrideCount() > 0 && <button onClick={resetPackageOverrides} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded transition-colors ${isDark ? 'text-amber-400 hover:bg-amber-400/10' : 'text-amber-600 hover:bg-amber-50'}`} data-id="element-791">
                    Reset to Package
                  </button>}

                <div className="flex items-center gap-3" data-id="element-792">
                  <div className="text-right flex items-baseline gap-1.5" data-id="element-793">
                    <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-794">
                      {viewedPackage.price === 0 ? 'Included' : `$${viewedPackage.price.toLocaleString()}`}
                    </span>
                    {savings > 0 && <span className={`text-[10px] line-through ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-795">
                        ${viewedPackage.originalPrice.toLocaleString()}
                      </span>}
                    {savings > 0 && <span className={`text-[9px] font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-796">
                        Save ${savings.toLocaleString()}
                      </span>}
                  </div>

                  {isActive ? <button onClick={handleRemovePackage} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`} data-id="element-797">
                      Remove
                    </button> : <button onClick={handleSelectPackage} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${isDark ? 'bg-cyan-500 text-white hover:bg-cyan-400' : 'bg-teal-600 text-white hover:bg-teal-500'}`} data-id="element-798">
                      Select
                    </button>}
                </div>
              </div>
            </div>

            {/* Appliance Thumbnails Row */}
            <div className="relative flex-1 min-h-0 flex" data-id="element-799">
              <div className="flex-1 relative min-w-0" data-id="element-800">
                <div className="h-full custom-scrollbar p-4 flex gap-3 content-start flex-nowrap overflow-x-auto" data-id="element-801">
                  {viewedPackage.items.map((item, index) => {
                const isOverridden = isActive && isItemOverridingPackage(item.category);
                return <div key={index} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-802">
                        <div className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-803">
                          <div className={`
                            relative rounded-xl overflow-hidden border-2 transition-all duration-300
                            ${docked ? 'w-28 h-28' : 'w-24 h-24'}
                            ${isActive ? isOverridden ? isDark ? 'border-amber-500/50' : 'border-amber-400' : isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}
                          `} data-id="element-804">
                            {item.thumbnail ? <img src={item.thumbnail} alt={item.name} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${isOverridden ? 'opacity-50 grayscale' : ''}`} data-id="element-805" /> : <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`} data-id="element-806">
                                <span className={`text-[10px] ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-807">
                                  No Image
                                </span>
                              </div>}
                            <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-808">
                              <MarqueeName name={item.category} isDark={isDark} data-id="element-809" />
                            </div>
                            {isActive && !isOverridden && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-810">
                                <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-811">
                                  <Check className="w-3 h-3 text-white" data-id="element-812" />
                                </div>
                              </div>}
                            {isOverridden && <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-amber-500 shadow-sm border border-white/20" title="Overridden" data-id="element-813" />}
                          </div>
                          <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-814">
                            <span className={`
                              text-[10px] font-bold px-2 py-0.5 rounded-full truncate max-w-full
                              ${isOverridden ? isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}
                            `} title={isOverridden ? 'Overridden' : item.name} data-id="element-815">
                              {isOverridden ? 'Overridden' : item.name}
                            </span>
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}